import React from "react";
import DatePicker from "react-datepicker";

interface DateRangeCalendarProps {
  selectedDates: Date[];
  datesInRange: string[];
  isCalendarOpen: boolean;
  setIsCalendarOpen: (open: boolean) => void;
  onDateChange: (date: Date | null) => void;
  onSaveDates: () => void;
  onClearDates: () => void;
  error?: string;
  // Optional popup placement/portal (the route search renders into a portal so
  // the calendar escapes overflow; the flight-number search does not).
  popperPlacement?: any;
  portalId?: string;
}

// The multi-date "Date" field: a react-datepicker with Clear-All / Save
// controls and an edit affordance once dates are chosen.
const DateRangeCalendar: React.FC<DateRangeCalendarProps> = ({
  selectedDates,
  datesInRange,
  isCalendarOpen,
  setIsCalendarOpen,
  onDateChange,
  onSaveDates,
  onClearDates,
  error,
  popperPlacement,
  portalId,
}) => (
  // The whole padded box opens the calendar (not just the input), matching the
  // airport fields. Guard against reopening when already open so a click that
  // bubbles up from inside the (non-portalled) calendar doesn't reopen it.
  <div
    className="relative flex-1 p-3 border border-[#E5E7EB] rounded-xl cursor-pointer transition-colors hover:bg-gray-50"
    onClick={() => {
      if (!isCalendarOpen) setIsCalendarOpen(true);
    }}
  >
    <label className="flex items-center gap-1.5 mb-1 text-xs text-[#6B7280] cursor-pointer">
      <i className="st-calendar text-[#6B7280] text-[11px]"></i>
      Date
    </label>
    <div className="relative">
      <DatePicker
        selected={null}
        onChange={onDateChange}
        onCalendarClose={() => setIsCalendarOpen(false)}
        onCalendarOpen={() => setIsCalendarOpen(true)}
        open={isCalendarOpen}
        onClickOutside={() => setIsCalendarOpen(false)}
        shouldCloseOnSelect={false}
        minDate={new Date()}
        placeholderText={`${datesInRange.length > 0 ? `Selected Dates (${datesInRange.length})` : "Select travel dates"}`}
        className={`block w-full py-1.5 border-none bg-transparent text-sm text-[#1F2937] font-medium ${datesInRange.length > 0 ? "placeholder-black" : "placeholder-gray-400"} focus:outline-none cursor-pointer`}
        highlightDates={selectedDates}
        inline={false}
        wrapperClassName="w-full"
        popperPlacement={popperPlacement}
        portalId={portalId}
        onInputClick={() => setIsCalendarOpen(true)}
      >
        <div className="flex justify-between w-full px-2 pt-2">
          <button
            onClick={(e) => {
              e.preventDefault();
              onClearDates();
            }}
            className="px-4 py-2 text-sm text-red-600 border border-red-600 rounded-lg hover:bg-red-50"
            type="button"
          >
            Clear All
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              onSaveDates();
            }}
            className="px-4 py-2 text-sm text-white bg-[#FFBC00] rounded-lg hover:bg-[#e6a800]"
            type="button"
          >
            OK
          </button>
        </div>
      </DatePicker>
      {datesInRange.length > 0 ? (
        <button
          onClick={() => setIsCalendarOpen(true)}
          className="absolute inset-y-0 end-0 flex items-center z-20 px-1 text-lg cursor-pointer text-gray-400 hover:text-blue-600 rounded-e-md focus:outline-none"
          type="button"
          title="Edit dates"
        >
          <i className="st-calendar text-lightgray-200"></i>
        </button>
      ) : (
        // Caret signals the field opens a picker (not a plain text input).
        <i className="st-arrow-down text-[10px] text-gray-400 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none"></i>
      )}
    </div>
    {error && <p className="text-red-500 text-[11px] mt-0.5">{error}</p>}
  </div>
);

export default DateRangeCalendar;
